import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

interface SavedPlacesState {
    items: { [id: string]: StoredPlace };
    loading: boolean;
    error: string | null;
}

export interface StoredPlace {
    id: string;
    name: string | null | undefined;
    city: string | null;
    country: string | null;
}

interface RoamEasyBackendPlace {
    userId: string,
    placeId: string,
    name: string,
    city: string,
    country: string,
  }

const initialState: SavedPlacesState = {
    items: {},
    loading: false,
    error: null,
};

const apiUrl = process.env.NODE_ENV === 'production' 
  ? 'https://api.roameasyapp.com' 
  : 'http://localhost:8080';

export const fetchInitialSavedPlaces = createAsyncThunk<StoredPlace[], string>(
    "savedPlaces/fetchInitialSavedPlaces",
    async (userId: string) => {
      const response = await fetch(`${apiUrl}/saved-places?userId=${userId}`);
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const result: RoamEasyBackendPlace[] =  await response.json();
      return result.map((item: RoamEasyBackendPlace) =>
        ({id: item.placeId, name: item.name, city: item.city, country: item.country}))
    }
);

export const addSavedPlaces = createAsyncThunk(
    "savedPlaces/addSavedPlaces",
    async(item: StoredPlace,thunkAPI) => {
      const state = thunkAPI.getState() as { userDetails: { userId: string } };
      const userId = state.userDetails.userId;
      const response = await fetch(`${apiUrl}/saved-places`, {
          method: 'POST',
          headers: {
          'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, placeId: item.id, name: item.name, city: item.city, country: item.country }),
      });
      if (!response.ok) {
          throw new Error('Failed to add place');
      }
      return item;
    }
);

export const deleteSavedPlace = createAsyncThunk(
  'savedPlaces/deleteSavedPlace',
  async(id: string, thunkAPI) => {
    const state = thunkAPI.getState() as { userDetails: { userId: string } };
      const userId = state.userDetails.userId;
    const response = await fetch(`${apiUrl}/saved-places`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId , placeId: id }),
    });
    if(!response.ok){
      throw new Error('Failed to delete place')
    }
    return id;
  }
);

const savedPlacesSlice = createSlice({
    name: 'savedPlaces',
    initialState,
    reducers: {
        resetSavedPlaces(state){
          state = {
            items: {},
            loading: false,
            error: null
          };
          return state;
        }

    },
    extraReducers: (builder) => {
        builder
          .addCase(fetchInitialSavedPlaces.pending, (state) => {
            state.loading = true;
            state.error = null; // Reset error on new request
          })
          .addCase(fetchInitialSavedPlaces.fulfilled, (state, action) => {
            state.loading = false;
            // Add item to local state
            state.items = action.payload.reduce((acc: { [id: string]: StoredPlace }, item: StoredPlace) =>{
                acc[item.id] = item;
                return acc;
            }, {});
          })
          .addCase(fetchInitialSavedPlaces.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message as string;
          });

        builder
          .addCase(addSavedPlaces.pending, (state) =>{
            state.loading = true;
            state.error = null; // Reset error on new request
          })
          .addCase(addSavedPlaces.fulfilled, (state, action) => {
            state.loading = false;
            state.items[action.payload.id] = action.payload;
          })
          .addCase(addSavedPlaces.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message as string;
          })

        builder
          .addCase(deleteSavedPlace.pending, (state) =>{
            state.loading = true;
            state.error = null; // Reset error on new request
          })
          .addCase(deleteSavedPlace.fulfilled, (state, action) => {
            state.loading = false;
            delete state.items[action.payload];
          })
          .addCase(deleteSavedPlace.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message as string;
          })
      }
});

export const { resetSavedPlaces } = savedPlacesSlice.actions;
export default savedPlacesSlice.reducer;
